import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { ThemePreference } from '@bulk/types';

const STORAGE_KEY = 'bulk.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Surfaces a person works in. The public gym site keeps the gym's own palette whatever the viewer prefers. */
export const THEMED_PATHS = /^\/(admin|app|platform|login|register|invite|member-link|offers|receipts|pay)(\/|$)/;

/** The colour of the browser's own chrome on a phone: the page background of Bulk's light and dark palettes. */
export const themeColor = (dark: boolean) => (dark ? '#121417' : '#f3f4f6');

interface ThemeState {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // storage unavailable
  }
  return 'system';
}

function systemDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [osDark, setOsDark] = useState(systemDark);
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => setOsDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: 'light' | 'dark' = preference === 'system' ? (osDark ? 'dark' : 'light') : preference;
  const themed = THEMED_PATHS.test(pathname);

  useEffect(() => {
    const root = document.documentElement;
    if (themed) root.dataset['theme'] = resolved;
    else delete root.dataset['theme'];
    if (!root.classList.contains('portal-look')) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor(themed && resolved === 'dark'));
  }, [themed, resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage unavailable
    }
    setPreferenceState(next);
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
